import cx from 'classnames'
import { makeStyles } from 'tss-react/mui'
import React, { FC } from 'react'
import ReactMarkdown, { UrlTransform } from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import classNames from 'classnames'
import { colors } from '@hazadapt-git/public-core-base'

export type MarkdownStyleOverrides = {
    p?: string
    h1?: string
    h2?: string
    h3?: string
    h4?: string
    h5?: string
    li?: string
    ul?: string
    ol?: string
    a?: string
    blockquote?: string
    strong?: string
}

interface MarkdownProps {
    containerClassName?: string
    content: string
    styleOverrides?: MarkdownStyleOverrides
    urlTransform?: UrlTransform
}

export const Markdown: FC<MarkdownProps> = ({
    containerClassName,
    content,
    styleOverrides = {},
    urlTransform,
}) => {
    const { classes: mdClasses } = useMdStyles()

    return (
        <div>
            <ReactMarkdown
                className={classNames(mdClasses.container, containerClassName)}
                urlTransform={urlTransform}
                rehypePlugins={[rehypeRaw]}
                components={{
                    h1: ({ node, children, ...elemProps }) => (
                        <h1
                            {...elemProps}
                            className={cx(mdClasses.h1, styleOverrides.h1)}
                            children={children}
                        />
                    ),
                    h2: ({ node, children, ...elemProps }) => (
                        <h2
                            {...elemProps}
                            className={cx(mdClasses.h2, styleOverrides.h2)}
                            children={children}
                        />
                    ),
                    h3: ({ node, children, ...elemProps }) => (
                        <h3
                            {...elemProps}
                            className={cx(mdClasses.h3, styleOverrides.h3)}
                            children={children}
                        />
                    ),
                    h4: ({ node, children, ...elemProps }) => (
                        <h4
                            {...elemProps}
                            className={cx(mdClasses.h4, styleOverrides.h4)}
                            children={children}
                        />
                    ),
                    h5: ({ node, children, ...elemProps }) => (
                        <h5
                            {...elemProps}
                            className={cx(mdClasses.h5, styleOverrides.h5)}
                            children={children}
                        />
                    ),
                    p: ({ node, ...elemProps }) => (
                        <p
                            className={cx(mdClasses.p, styleOverrides.p)}
                            {...elemProps}
                        />
                    ),
                    li: ({ node, ...elemProps }) => (
                        <li
                            className={cx(mdClasses.li, styleOverrides.li)}
                            {...elemProps}
                        />
                    ),
                    ul: ({ node, ...elemProps }) => (
                        <ul
                            className={cx(mdClasses.ul, styleOverrides.ul)}
                            {...elemProps}
                        />
                    ),
                    ol: ({ node, ...elemProps }) => (
                        <ol
                            className={cx(mdClasses.ol, styleOverrides.ol)}
                            {...elemProps}
                        />
                    ),
                    a: ({ node, children, ...elemProps }) => (
                        <a
                            {...elemProps}
                            className={cx(mdClasses.a, styleOverrides.a)}
                            children={children}
                        />
                    ),
                    blockquote: ({ node, ...elemProps }) => (
                        <blockquote
                            className={cx(
                                mdClasses.blockquote,
                                styleOverrides.blockquote
                            )}
                            {...elemProps}
                        />
                    ),
                    strong: ({ node, ...elemProps }) => (
                        <strong
                            className={cx(
                                mdClasses.strong,
                                styleOverrides.strong
                            )}
                            {...elemProps}
                        />
                    ),
                    hr: ({ node, ...elemProps }) => (
                        <hr
                            {...elemProps}
                            style={{
                                border: 'none',
                                borderTop: `1px solid ${colors.grays.CUMULUS}`,
                                margin: '0 0 0.5rem',
                            }}
                        />
                    ),
                }}
                children={content}
            />
        </div>
    )
}

export const useMdStyles = makeStyles()({
    container: {
        display: 'flex',
        flexDirection: 'column',
    },
    p: {
        fontSize: '1rem',
        margin: 0,
        padding: '0 0 0.5rem',
        ':last-child': {
            padding: 0,
        },
    },
    ul: {
        paddingTop: 0,
        marginTop: 0,
        paddingBottom: 0,
        marginBottom: 0,
    },
    ol: {
        paddingTop: 0,
        marginTop: 0,
        paddingBottom: 0,
        marginBottom: 0,
    },
    li: {
        padding: '0 0.15625rem',
        margin: 0,
    },
    a: {
        color: 'blue',
    },
    blockquote: {
        paddingBottom: '0.625rem',
    },
    strong: {
        fontWeight: 600,
    },
    h1: {
        fontSize: '2rem',
        fontWeight: 600,
        color: 'black',
        margin: 0,
        padding: 0,
    },
    h2: {
        fontSize: '1.8rem',
        fontWeight: 600,
        color: 'black',
        margin: 0,
        padding: 0,
    },
    h3: {
        fontSize: '1.5rem',
        fontWeight: 600,
        color: 'black',
        margin: 0,
        padding: 0,
    },
    h4: {
        fontSize: '1.2rem',
        fontWeight: 500,
        color: 'black',
        margin: 0,
        padding: 0,
    },
    h5: {
        fontSize: '1rem',
        fontStyle: 'italic',
        color: 'black',
        margin: 0,
        padding: 0,
    },
})
